/**
 * Dynamic SEO keyword builder for public salon website.
 *
 * Generates a clean, deduplicated, naturally ordered keyword set per language.
 * Combines:
 *   - Base universal beauty/salon keywords (curated AR + EN sets)
 *   - Salon-specific (name, city, services, category)
 *   - Admin-supplied custom keywords from CMS
 *
 * Anti-spam: dedupes (case-insensitive), trims, removes empties, caps at MAX_KEYWORDS.
 * Outputs:
 *   - meta keywords string (CSV)
 *   - shortlist for inline natural inclusion in title/description
 */

const MAX_KEYWORDS = 18; // sweet spot — Google ignores keyword stuffing past ~15

// ─────────────────────────────────────────────
// Curated base keyword sets
// ─────────────────────────────────────────────
export const BASE_KEYWORDS_AR = [
  "صالون تجميل",
  "صالون نسائي",
  "مركز تجميل",
  "حجز صالون",
  "حجز موعد صالون",
  "حجز اونلاين",
  "حجز صالون اونلاين",
  "تطبيق صالونات",
  "نظام صالونات",
  "برنامج صالونات",
  "إدارة صالونات",
  "أفضل صالون",
  "أفضل صالون نسائي",
  "صالون فاخر",
  "صالون راقي",
  "صالون في الدوحة",
  "صالون في قطر",
  "صالون نسائي في قطر",
  "صالون نسائي في الدوحة",
  "صالون قريب مني",
  "خدمات تجميل",
  "خدمات العناية بالشعر",
  "خدمات العناية بالبشرة",
  "العناية بالشعر",
  "العناية بالبشرة",
  "العناية بالأظافر",
  "العناية بالجسم",
  "صبغات شعر",
  "قص شعر",
  "تصفيف شعر",
  "استشوار",
  "كيراتين",
  "بروتين شعر",
  "علاج الشعر",
  "تنظيف البشرة",
  "فيلر",
  "بوتوكس",
  "حمام مغربي",
  "مساج",
  "سبا",
  "مانيكير",
  "باديكير",
  "رموش",
  "ميك اب",
  "مكياج",
  "تجميل",
  "صالون عرائس",
  "تجهيز عرائس",
  "مركز سبا",
];

export const BASE_KEYWORDS_EN = [
  "spa",
  "beauty salon",
  "beauty center",
  "hair salon",
  "women salon",
  "ladies salon",
  "salon booking",
  "online salon booking",
  "beauty booking",
  "salon management system",
  "beauty management software",
  "beauty services",
  "hair styling",
  "hair coloring",
  "hair treatment",
  "keratin treatment",
  "protein treatment",
  "facial treatment",
  "skin care",
  "nail care",
  "spa services",
  "massage",
  "makeup artist",
  "bridal makeup",
  "bridal salon",
  "manicure",
  "pedicure",
  "lashes",
  "beauty spa",
  "best beauty salon",
  "best salon in Doha",
  "beauty salon in Qatar",
  "beauty salon in Doha",
  "ladies beauty salon",
  "salon near me",
  "beauty services online booking",
  "online appointment booking",
];

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────
const normalize = (s) =>
  String(s || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const dedupe = (arr) => {
  const seen = new Set();
  const out = [];
  for (const k of arr) {
    const original = String(k || "").trim();
    if (!original) continue;
    const key = normalize(original);
    if (!seen.has(key) && key.length >= 2) {
      seen.add(key);
      out.push(original);
    }
  }
  return out;
};

const parseKeywordsField = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  // accept comma, newline, or semicolon-separated
  return String(raw)
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

// Smart pick: ensures locality-related keywords come first, then services, then base
const smartShuffle = (keywords, lang, city) => {
  const cityLower = normalize(city);
  const isAR = lang === "ar";
  const buckets = { local: [], service: [], brand: [], base: [] };
  const cityNeedles = isAR
    ? [cityLower, "الدوحة", "قطر", "قريب مني"]
    : [cityLower, "doha", "qatar", "near me"];

  const isLocal = (k) => cityNeedles.some((n) => n && normalize(k).includes(n));
  const isBaseAR = new Set(BASE_KEYWORDS_AR.map(normalize));
  const isBaseEN = new Set(BASE_KEYWORDS_EN.map(normalize));
  const isBase = (k) => (isAR ? isBaseAR.has(normalize(k)) : isBaseEN.has(normalize(k)));

  for (const k of keywords) {
    if (isLocal(k)) buckets.local.push(k);
    else if (isBase(k)) buckets.base.push(k);
    else buckets.service.push(k);
  }
  return [...buckets.local, ...buckets.service, ...buckets.brand, ...buckets.base];
};

// ─────────────────────────────────────────────
// Main public API
// ─────────────────────────────────────────────
/**
 * @param {Object} opts
 * @param {"ar"|"en"} opts.lang
 * @param {string} opts.salonName
 * @param {string} [opts.city]
 * @param {string} [opts.category]
 * @param {Array<{name_ar?:string, name_en?:string}>} [opts.services]
 * @param {string|string[]} [opts.customKeywords]   admin-supplied via CMS
 * @returns {{ keywords: string[], csv: string, top: string[] }}
 */
export function buildKeywords({
  lang = "ar",
  salonName = "",
  city = "",
  category = "",
  services = [],
  customKeywords = "",
} = {}) {
  const isAR = lang === "ar";
  const out = [];

  // 1. admin custom keywords first (highest signal)
  parseKeywordsField(customKeywords).forEach((k) => out.push(k));

  // 2. salon-specific phrases
  if (salonName) {
    out.push(salonName);
    if (city) {
      out.push(isAR ? `${salonName} ${city}` : `${salonName} ${city}`);
    }
  }
  if (category) out.push(category);

  // 3. services as keywords
  for (const s of services || []) {
    const n = isAR ? s.name_ar : s.name_en;
    if (n) out.push(String(n).trim());
    // pair with city for location-targeted long-tail
    if (n && city) {
      out.push(isAR ? `${n} في ${city}` : `${n} in ${city}`);
    }
  }

  // 4. city long-tails from base
  if (city) {
    if (isAR) {
      out.push(`صالون في ${city}`);
      out.push(`صالون نسائي في ${city}`);
      out.push(`أفضل صالون في ${city}`);
    } else {
      out.push(`beauty salon in ${city}`);
      out.push(`hair salon in ${city}`);
      out.push(`best salon in ${city}`);
    }
  }

  // 5. base keywords (lower priority — appended last)
  const base = isAR ? BASE_KEYWORDS_AR : BASE_KEYWORDS_EN;
  base.forEach((k) => out.push(k));

  // 6. dedupe + smart-order + cap
  const deduped = dedupe(out);
  const ordered = smartShuffle(deduped, lang, city);
  const final = ordered.slice(0, MAX_KEYWORDS);

  return {
    keywords: final,
    csv: final.join(", "),
    top: final.slice(0, 5),
  };
}

/**
 * Builds an SEO-friendly slug from a string.
 * - Arabic: keeps Arabic letters, replaces spaces with -
 * - Latin: lower-case + kebab
 */
export function slugify(s) {
  if (!s) return "";
  return String(s)
    .trim()
    .toLowerCase()
    .replace(/[^\u0600-\u06FFa-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 80);
}

/**
 * Crafts a natural meta description that includes top keywords organically.
 */
export function buildDescription({ lang, salonName, city, services = [], topKeywords = [] }) {
  const isAR = lang === "ar";
  const svcNames = services
    .slice(0, 4)
    .map((s) => (isAR ? s.name_ar : s.name_en))
    .filter(Boolean)
    .join("، ");

  if (isAR) {
    const cityPart = city ? ` في ${city}` : "";
    const svcPart = svcNames ? ` نقدم ${svcNames}.` : "";
    const tail = topKeywords.length ? ` ${topKeywords.slice(0, 3).join(" · ")}.` : "";
    return `${salonName || "صالون التجميل"}${cityPart} — احجزي موعدك أونلاين بسهولة.${svcPart}${tail}`.trim().slice(0, 160);
  }
  const cityPart = city ? ` in ${city}` : "";
  const svcPart = svcNames ? ` Featured services: ${svcNames}.` : "";
  const tail = topKeywords.length ? ` ${topKeywords.slice(0, 3).join(" · ")}.` : "";
  return `${salonName || "Beauty Salon"}${cityPart} — book your appointment online with ease.${svcPart}${tail}`.trim().slice(0, 160);
}
